<template>
  <div
    class="pinwall__video"
    v-observe-visibility="
      observeVisibility
        ? {
            callback: visibilityChanged,
            throttle: 1500,
            intersection: {
              threshold: 0,
              rootMargin: '-50% 0% -50% 0%'
            }
          }
        : false
    "
  >
    <button v-if="!post.playMedia" @click="play" class="play-button">
      <i class="play-icon material-icons-round">play_arrow</i>
    </button>
    <div v-if="post.video.type === 'youtube#video'">
      <div class="youtube-iframe-image">
        <PostImage
          v-if="!post.playMedia"
          :proportion="post.proportion"
          :image="post.image"
          :translateY="translateY"
        />
      </div>
      <div v-if="post.playMedia" class="youtube">
        <youtube
          @ended="ended"
          @ready="ready"
          :ref="`youtube-${post.id}`"
          :player-vars="{ autoplay: autoplay }"
          :video-id="post.video.source"
        />
      </div>
    </div>
    <div v-if="post.video.type === 'interactiveVideo'">
      <div class="iframe-image">
        <PostImage
          v-if="!post.playMedia"
          :proportion="post.proportion"
          :image="post.image"
        />
      </div>
      <InteractiveVideo v-if="post.playMedia" :id="post.video.source" />
    </div>
    <div
      v-if="
        post.video.type === 'local' ||
          post.video.type === 'giphy' ||
          post.video.type === 'srgLivestream'
      "
    >
      <PostImage
        v-if="!post.playMedia"
        :proportion="post.proportion"
        :image="post.image"
      />
      <Letterbox
        v-if="post.playMedia && post.video.type === 'srgLivestream'"
        :urn="post.video.source"
      ></Letterbox>
      <video
        @ended="ended"
        v-else-if="post.playMedia && post.video.type !== 'giphy'"
        :src="src"
        @loadstart="applyHls($event)"
        controls
        :style="getHeightStyle()"
        :autoplay="autoplay"
        :muted="muted"
        :loop="playingMode === 'replay'"
        playsinline
      />
      <video
        @ended="ended"
        v-else-if="post.playMedia"
        :src="post.video.source"
        :autoplay="autoplay"
        :muted="muted"
        :style="getHeightStyle()"
        loop
        playsinline
      />
    </div>
  </div>
</template>

<script>
import PostImage from "../pinwall/PostImage";
import InteractiveVideo from "../core/InteractiveVideo";
import Letterbox from "../core/Letterbox.vue";
import Hls from "hls.js/dist/hls.min.js";
import { mapGetters, mapMutations } from "vuex";
import { checkIfMobileOrTablet, checkIfiOsDevice } from "../../utils/helpers";

export default {
  name: "Video",
  props: {
    post: {
      type: Object
    },
    autoplay: {
      type: Boolean,
      default: true
    },
    muted: {
      type: Boolean,
      default: false
    },
    view: {
      type: String,
      default: null
    }
  },
  data: () => {
    return {
      hlsInstance: null,
      width: 0,
      initialHeight: null,
      margin: 0,
      translateY: null,
      observeVisibility: false
    };
  },
  watch: {
    post: {
      handler(post) {
        if (!post.playMedia && this.hlsInstance) {
          this.hlsInstance.destroy();
          this.hlsInstance = null;
        }
      },
      deep: true
    },
    "post.w": "setTranslateY",
    windowWidth: "setTranslateY"
  },
  computed: {
    ...mapGetters([
      "userClickedOnMedia",
      "playingPost",
      "playingMode",
      "tenant"
    ]),
    heightStyle() {
      if (this.width > 0 && this.post.proportion > 0) {
        return `height: ${Math.round(this.width / this.post.proportion)}px`;
      }
      return "";
    }
  },
  components: {
    InteractiveVideo,
    Letterbox,
    PostImage
  },
  beforeDestroy() {
    if (this.hlsInstance) {
      this.hlsInstance.destroy();
      this.hlsInstance = null;
    }
  },
  created() {
    if (this.post.video.type === "local") {
      this.src = `${process.env.VUE_APP_BASE_URL.toCorrectDomain()}/${
        process.env.VUE_APP_VIDEO_CDN_URL
      }${this.post.video.fileName.split(".")[0]}/master.m3u8`;
    }

    if (
      this.view === "list" &&
      this.post.video &&
      this.tenant.tenantId !== "srf" &&
      this.tenant.tenantId !== "rts" &&
      this.tenant.tenantId !== "rsi"
    ) {
      this.observeVisibility = true;
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.width = this.$el.clientWidth;
      this.setTranslateY();
    });
  },
  methods: {
    ...mapMutations(["setUserClickedOnMedia", "setPlayingPost"]),
    ended() {
      if (this.playingMode === "playlist-play" || this.view === "screen") {
        this.$emit("ended");
        return;
      }
      if (
        this.playingMode === "replay" &&
        this.post.video.type === "youtube#video"
      ) {
        var player = this.$refs[`youtube-${this.post.id}`].player;
        if (player) {
          player.seekTo(0);
        }
      }
    },
    setTranslateY(retry) {
      if (this.post.video.type !== "youtube#video") {
        return;
      }
      this.width = this.$el ? this.$el.clientWidth : 5;
      if (this.width > 100) {
        let factor = this.width >= 380 ? 0.01 : 0.0085;
        this.translateY =
          20 - this.width * (factor - this.width / (400000 - this.width * 162));
      } else {
        this.translateY = 20;
      }
      if (this.width === 5 && (!retry || retry < 3)) {
        this.$nextTick(() => {
          this.setTranslateY(retry ? (retry += 1) : 1);
        });
      }
    },
    getHeightStyle(retry) {
      this.width = this.$el ? this.$el.clientWidth : 5;
      if (this.width > 100 && this.post.proportion > 0) {
        return `height: ${Math.round(this.width / this.post.proportion)}px`;
      }

      if (
        this.width === 5 &&
        this.post.proportion > 0 &&
        (!retry || retry < 3)
      ) {
        this.$nextTick(() => {
          this.getHeightStyle(retry ? (retry += 1) : 1);
        });
      }
      return "";
    },
    applyHls(event) {
      let video = event.srcElement;
      if (Hls.isSupported() && !this.hlsInstance) {
        this.hlsInstance = new Hls();
        this.hlsInstance.loadSource(this.src);
        this.hlsInstance.attachMedia(video);
        this.hlsInstance.on(Hls.Events.MANIFEST_PARSED, function() {
          video.play();
        });
      }
    },
    visibilityChanged(isVisible) {
      if (
        window.fullScreen ||
        (window.innerWidth == screen.width &&
          window.innerHeight == screen.height)
      ) {
        return;
      }

      if (this.view && this.view === "list") {
        if (this.post.video) {
          if (
            isVisible &&
            (this.userClickedOnMedia ||
              this.post.video.type !== "youtube#video")
          ) {
            this.play();
          } else {
            this.post.playMedia = false;
          }
        }
      }
    },
    ready() {
      if (this.view === "list") {
        if (checkIfMobileOrTablet(navigator.userAgent)) {
          var player = this.$refs[`youtube-${this.post.id}`].player;
          if (player) {
            if (checkIfiOsDevice(navigator.userAgent)) {
              player.mute();
            }
            player.playVideo();
          }
        }
      }
    },
    play() {
      this.post.playMedia = true;
      if (!this.userClickedOnMedia) {
        this.setUserClickedOnMedia(true);
      }
      this.setPlayingPost(this.post);
    }
  }
};
</script>
